import * as yup from "yup";
import { CompanyDTO, Country, LegalForm } from "@/api/data-contracts";
import { useMemo } from "react";
import { countryOptions, legalFormOptions, useLegalFormsByCountry } from "@/datacaches";
import { Constants } from "@/api/constants";
import { BIC_REGEX, COMMERCIAL_REGISTRATION_NUMBER_REGEX, IBAN_REGEX, URL_REGEX, VAT_ID_REGEX } from "@/regex";
import { useTranslation } from "react-i18next";
import { CompanyI18N } from "@/translations";
import { allowedLegalForms } from "@/model/legalForm";

export type CompanyEditDTO = Pick<
    CompanyDTO,
    | "name"
    | "legalForm"
    | "companyAddress"
    | "website"
    | "commercialRegistrationNumber"
    | "vatIdentificationNumber"
    | "erpManufacturer"
    | "erpSoftwareName"
    | "erpSoftwareVersion"
    | "maschineryFleet"
    | "officeHours"
    | "paymentTerms"
    | "additionalInformation"
    | "bankName"
    | "iban"
    | "bic"
>;

export const useCompanyEditSchema = () => {
    const { t } = useTranslation();
    const [legalFormsByCountry] = useLegalFormsByCountry();
    return useMemo(() => {
        const schema = yup.object().shape({
            name: yup
                .string()
                .required(t(CompanyI18N.form.validation.nameRequired))
                .min(
                    Constants.COMPANY_NAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.nameMin, { min: Constants.COMPANY_NAME_LENGTH_MIN })
                )
                .max(
                    Constants.COMPANY_NAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.nameMax, { max: Constants.COMPANY_NAME_LENGTH_MAX })
                ),

            legalForm: yup
                .string()
                .required(t(CompanyI18N.form.validation.legalFormRequired))
                .oneOf(legalFormOptions, t(CompanyI18N.form.validation.legalFormInvalid))
                .test(
                    "legal-form-matches-country",
                    t(CompanyI18N.form.validation.legalFormForCountryInvalid),
                    function (value) {
                        // Required-ness is enforced separately above — an empty value is not this test's concern.
                        if (!value) {
                            return true;
                        }
                        return allowedLegalForms(this.parent.companyAddress?.country, legalFormsByCountry).includes(
                            value as LegalForm
                        );
                    }
                ),

            companyAddress: yup.object().shape({
                country: yup
                    .string()
                    .required(t(CompanyI18N.form.validation.countryRequired))
                    .oneOf(countryOptions, t(CompanyI18N.form.validation.countryInvalid))
            }),

            website: yup
                .string()
                .optional()
                .nullable()
                .transform((value) => value || null)
                .min(
                    Constants.WEBSITE_LENGTH_MIN,
                    t(CompanyI18N.form.validation.websiteMin, { min: Constants.WEBSITE_LENGTH_MIN })
                )
                .max(
                    Constants.WEBSITE_LENGTH_MAX,
                    t(CompanyI18N.form.validation.websiteMax, { max: Constants.WEBSITE_LENGTH_MAX })
                )
                .matches(URL_REGEX, t(CompanyI18N.form.validation.websiteInvalid)),

            commercialRegistrationNumber: yup
                .string()
                .when("companyAddress.country", {
                    is: (country: Country) => country === Country.GERMANY,
                    then: (schema) => schema.required(t(CompanyI18N.form.validation.commercialRegisterNumberRequired)),
                    otherwise: (schema) => schema.optional().nullable()
                })
                .transform((value) => value || null)
                .min(
                    Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MIN,
                    t(CompanyI18N.form.validation.commercialRegisterNumberMin, {
                        min: Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MIN
                    })
                )
                .max(
                    Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX,
                    t(CompanyI18N.form.validation.commercialRegisterNumberMax, {
                        max: Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX
                    })
                )
                .matches(
                    COMMERCIAL_REGISTRATION_NUMBER_REGEX,
                    t(CompanyI18N.form.validation.commercialRegisterNumberInvalid)
                ),

            vatIdentificationNumber: yup
                .string()
                .required(t(CompanyI18N.form.field.vatId))
                .min(
                    Constants.VAT_ID_LENGTH_MIN,
                    t(CompanyI18N.form.validation.vatIdMin, { min: Constants.VAT_ID_LENGTH_MIN })
                )
                .max(
                    Constants.VAT_ID_LENGTH_MAX,
                    t(CompanyI18N.form.validation.vatIdMax, { max: Constants.VAT_ID_LENGTH_MAX })
                )
                .matches(VAT_ID_REGEX, t(CompanyI18N.form.validation.vatIdInvalid)),

            erpManufacturer: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpManufacturerRequired))
                .min(
                    Constants.ERP_MANUFACTURER_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpManufacturerMin, { min: Constants.ERP_MANUFACTURER_LENGTH_MIN })
                )
                .max(
                    Constants.ERP_MANUFACTURER_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpManufacturerMax, { max: Constants.ERP_MANUFACTURER_LENGTH_MAX })
                ),

            erpSoftwareName: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpSoftwareNameRequired))
                .min(
                    Constants.ERP_SOFTWARE_NAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpSoftwareNameMin, { min: Constants.ERP_SOFTWARE_NAME_LENGTH_MIN })
                )
                .max(
                    Constants.ERP_SOFTWARE_NAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpSoftwareNameMax, { max: Constants.ERP_SOFTWARE_NAME_LENGTH_MAX })
                ),

            erpSoftwareVersion: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpSoftwareVersionRequired))
                .min(
                    Constants.ERP_SOFTWARE_VERSION_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpSoftwareVersionMin, {
                        min: Constants.ERP_SOFTWARE_VERSION_LENGTH_MIN
                    })
                )
                .max(
                    Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpSoftwareVersionMax, {
                        max: Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX
                    })
                ),

            maschineryFleet: yup.object().shape({
                miniliner: yup.boolean(),

                midliner: yup.boolean(),

                maxiliner: yup.boolean(),

                dieCutting: yup.boolean(),

                folderGluerMachine: yup.boolean(),

                packaging: yup.boolean(),

                hazardousGoodsQualification: yup.boolean(),

                flexoPrinting: yup.boolean(),

                hdFlexoPrinting: yup.boolean(),

                offsetPrinting: yup.boolean(),

                digitalPrinting: yup.boolean()
            }),

            paymentTerms: yup
                .string()
                .required(t(CompanyI18N.form.validation.paymentTermsRequired))
                .min(
                    Constants.PAYMENT_TERMS_LENGTH_MIN,
                    t(CompanyI18N.form.validation.paymentTermsMin, { min: Constants.PAYMENT_TERMS_LENGTH_MIN })
                )
                .max(
                    Constants.PAYMENT_TERMS_LENGTH_MAX,
                    t(CompanyI18N.form.validation.paymentTermsMax, { max: Constants.PAYMENT_TERMS_LENGTH_MAX })
                ),

            additionalInformation: yup
                .string()
                .optional()
                .nullable()
                .transform((value) => value || null)
                .max(
                    Constants.ADDITIONAL_INFORMATION_LENGTH_MAX,
                    t(CompanyI18N.form.validation.additionalInformationMax, {
                        max: Constants.ADDITIONAL_INFORMATION_LENGTH_MAX
                    })
                ),

            bankName: yup
                .string()
                .required(t(CompanyI18N.form.validation.bankNameRequired))
                .min(
                    Constants.BANKNAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.bankNameMin, { min: Constants.BANKNAME_LENGTH_MIN })
                )
                .max(
                    Constants.BANKNAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.bankNameMax, { max: Constants.BANKNAME_LENGTH_MAX })
                ),

            iban: yup
                .string()
                .required(t(CompanyI18N.form.validation.ibanRequired))
                .min(
                    Constants.IBAN_LENGTH_MIN,
                    t(CompanyI18N.form.validation.ibanMin, { min: Constants.IBAN_LENGTH_MIN })
                )
                .max(
                    Constants.IBAN_LENGTH_MAX,
                    t(CompanyI18N.form.validation.ibanMax, { max: Constants.IBAN_LENGTH_MAX })
                )
                .matches(IBAN_REGEX, t(CompanyI18N.form.validation.ibanInvalid)),

            bic: yup
                .string()
                .required(t(CompanyI18N.form.validation.bicRequired))
                .min(Constants.BIC_LENGTH_MIN, t(CompanyI18N.form.validation.bicMin, { min: Constants.BIC_LENGTH_MIN }))
                .max(Constants.BIC_LENGTH_MAX, t(CompanyI18N.form.validation.bicMax, { max: Constants.BIC_LENGTH_MAX }))
                .matches(BIC_REGEX, t(CompanyI18N.form.validation.bicInvalid))
        });

        const typed = schema as yup.ObjectSchema<CompanyEditDTO>;
        return typed as yup.ObjectSchema<CompanyDTO>;
    }, [t, legalFormsByCountry]);
};
